import { isRetiredSetup } from './setupRetirement.js'
import { hasSetupDetails } from './setupPresence.js'

export const canRestoreSetup = project => isRetiredSetup(project)
  && hasSetupDetails({ ...project, setup_status: 'idea', schwab_order_ids: [] })

// Restore planning fields into a new identity. Never revive broker references,
// trade links, execution state, or the original canceled record.
export async function restoreSetupAsIdea(supabase, source, { userId, newId }) {
  if (!userId || source?.user_id !== userId || !source?.id || !newId || newId === source.id) {
    throw new Error('Setup identity is unavailable. Refresh before restoring.')
  }
  const { data: current, error: readError } = await supabase.from('position_projects')
    .select('*').eq('id', source.id).eq('user_id', userId).single()
  if (readError || !canRestoreSetup(current) || current.user_id !== userId) {
    throw new Error('This historical setup is unavailable or has no saved details to restore.')
  }
  const fields = {}
  for (const key of ['setup_pattern', 'setup_type', 'setup_source', 'setup_notes', 'entry_type', 'trigger_price', 'pattern_failure_price', 'sell_target_price', 'recommended_size_pct']) {
    if (current[key] != null) fields[key] = current[key]
  }
  const payload = {
    ...fields,
    id: newId, user_id: userId, symbol: current.symbol, direction: current.direction || 'LONG',
    stage: 'idea', setup_status: 'idea', is_draft: false, trade_id: null, schwab_order_ids: [],
    restored_from_setup_id: current.id,
    account_sizing: (Array.isArray(current.account_sizing) ? current.account_sizing : []).map(row => ({
      account_number: row.account_number, shares: row.shares, max_loss: row.max_loss,
    })),
  }
  const { data, error } = await supabase.from('position_projects').insert(payload).select('*').single()
  if (!error && data?.id === newId) return data
  // Reuse the same generated ID on retry after an uncertain network result.
  // A duplicate insert must not create a second restored idea.
  if (error?.code === '23505') {
    const { data: existing, error: lookupError } = await supabase.from('position_projects')
      .select('*').eq('restored_from_setup_id', current.id).eq('user_id', userId).single()
    if (!lookupError && existing?.user_id === userId && existing.symbol === current.symbol && existing.restored_from_setup_id === current.id) return existing
  }
  throw new Error(error?.message || 'Could not restore this setup. Please retry.')
}

export function visibleSetupHistory(history, allSymbolSetups) {
  return history.filter(source => !allSymbolSetups.some(row =>
    row.restored_from_setup_id === source.id && row.user_id === source.user_id && row.symbol === source.symbol,
  ))
}
